import {createTheme} from "@mui/material/styles";
import {CommonThemeColors, DEFAULT_COLOR_THEME, theme as baseTheme,} from "crqlar-react-common/dist/components/theme";
import type {OrganizationTheme} from "crqlar-react-common/dist/models/organization/organization";

export const createDashboardTheme = (organizationTheme?: OrganizationTheme) => {

    const resolvedOrganizationTheme = organizationTheme ?? DEFAULT_COLOR_THEME;

    const dashboardTheme = createTheme({
        ...baseTheme,
        palette: {
            ...baseTheme.palette,
            primary: {
                ...baseTheme.palette.primary,
                ...resolvedOrganizationTheme.primary,
            },
            secondary: {
                ...baseTheme.palette.secondary,
                ...resolvedOrganizationTheme.secondary,
            },
            action: {
                ...baseTheme.palette.action,
                hover: resolvedOrganizationTheme.primary?.hover ?? DEFAULT_COLOR_THEME.primary.hover,
                selected: resolvedOrganizationTheme.primary?.selected ?? DEFAULT_COLOR_THEME.primary.selected,
                focus: resolvedOrganizationTheme.primary?.focus ?? DEFAULT_COLOR_THEME.primary.focus,
                disabled: CommonThemeColors.sys.action.disabled,
                disabledBackground: CommonThemeColors.sys.action.disabledBackground,
            },
            text: {
                primary: resolvedOrganizationTheme.text?.primary ?? DEFAULT_COLOR_THEME.text.primary,
                secondary: resolvedOrganizationTheme.text?.secondary ?? DEFAULT_COLOR_THEME.text.secondary,
                disabled: resolvedOrganizationTheme.text?.disabled ?? DEFAULT_COLOR_THEME.text.disabled,
            },
        },
        components: {
            ...baseTheme.components,
            MuiIconButton: {
                styleOverrides: {
                    root: ({theme}) => ({
                        '&[data-dashboard-control]': {
                            color: theme.palette.primary.main,
                            backgroundColor: 'transparent',
                            '&:hover': {
                                color: theme.palette.primary.dark,
                                backgroundColor: theme.palette.action.hover,
                            },
                            '&.Mui-focusVisible': {
                                outline: `2px solid ${theme.palette.primary.main}`,
                                outlineOffset: 2,
                            },
                            '& .MuiBadge-badge': {
                                color: theme.palette.primary.contrastText,
                                backgroundColor: theme.palette.primary.main,
                                boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
                            },
                            '& .MuiCircularProgress-root': {
                                color: 'inherit',
                            },
                            '@media (forced-colors: active)': {
                                color: 'ButtonText',
                                backgroundColor: 'ButtonFace',
                                border: '1px solid ButtonBorder',
                                '&.Mui-disabled': {
                                    color: 'GrayText',
                                },
                            },
                        },
                        '&[data-dashboard-control][data-active="true"]': {
                            color: theme.palette.primary.contrastText,
                            backgroundColor: theme.palette.primary.main,
                            '&:hover': {
                                color: theme.palette.primary.contrastText,
                                backgroundColor: theme.palette.primary.dark,
                            },
                            '@media (forced-colors: active)': {
                                color: 'SelectedItemText',
                                backgroundColor: 'SelectedItem',
                            },
                        },
                        '&[data-dashboard-control].Mui-disabled': {
                            color: theme.palette.action.disabled,
                            backgroundColor: 'transparent',
                        },
                    }),
                },
            },
            MuiCssBaseline: {
                styleOverrides: `
                    @media print {
                        .MuiTooltip-popper,
                        .MuiTooltip-portal,
                        [role="tooltip"] {
                            display: none !important;
                            visibility: hidden !important;
                            pointer-events: none !important;
                        }
                    }
                `,
            },
            // Close on button-leave instead of catching the cursor; none of our
            // tooltips host interactive content that needs hovering into.
            MuiTooltip: {
                ...baseTheme.components?.MuiTooltip,
                defaultProps: {disableInteractive: true},
            },
        },
    });

    // Keep legacy direct color references synchronized with the active organization theme.
    CommonThemeColors.sys.primary = {
        ...DEFAULT_COLOR_THEME.primary,
        ...resolvedOrganizationTheme.primary,
    };
    CommonThemeColors.sys.secondary = {
        ...DEFAULT_COLOR_THEME.secondary,
        ...resolvedOrganizationTheme.secondary,
    };
    CommonThemeColors.sys.text = {
        ...DEFAULT_COLOR_THEME.text,
        ...resolvedOrganizationTheme.text,
    };

    return dashboardTheme;
};
